/**
 * ============================================================================
 * STREAM DO WORKSPACE (SSE).  O único lugar do painel que abre conexão viva.
 * ============================================================================
 *
 * A api publica incidentes num canal por workspace e entrega por
 * `GET /events?workspaceId=...` (`apps/api/src/routes/events.ts`). Aqui é o lado
 * do navegador: um hook que abre a conexão, entrega evento por evento e some
 * sem deixar nada aberto.
 *
 * ### Três armadilhas que este arquivo existe para evitar
 *
 * **1. A tela que monta duas vezes.** Em desenvolvimento o React roda o efeito,
 * desmonta e roda de novo (StrictMode). Este projeto já foi mordido por isso
 * com canal de realtime de nome fixo: a segunda montagem derrubava a conexão
 * da primeira e a página ficava muda. A defesa aqui é não ter estado no módulo
 * — cada montagem tem a SUA `EventSource`, guardada dentro do efeito, e a
 * limpeza fecha exatamente aquela. Duas montagens seguidas deixam uma conexão
 * viva, nunca duas e nunca zero.
 *
 * **2. A função que muda de identidade a cada render.** `onEvent` costuma vir
 * como arrow inline (`onEvent={(e) => setX(e)}`), ou seja, uma função nova a
 * cada render. Se ela entrasse nas dependências do efeito, a conexão seria
 * derrubada e reaberta a cada render — um laço de reconexão que parece "SSE
 * instável" e é bug nosso. Por isso ela vive numa `ref`.
 *
 * **3. A reconexão que nunca acontece.** O `EventSource` reconecta sozinho
 * enquanto a falha é de rede, mas desiste em definitivo quando a resposta não
 * é um `text/event-stream` válido — o que acontece justamente no caso mais
 * comum: a api reiniciando (502 do proxy por alguns segundos). Aí o painel
 * fica calado para sempre sem nenhum erro na tela. A defesa é reconectar por
 * conta própria, com espera crescente e sorteada.
 */
import { useEffect, useRef } from 'react';
import { z } from 'zod';

import { API_ORIGIN } from './api';

export const STREAM_EVENT_KINDS = [
  'incident.opened',
  'incident.updated',
  'incident.resolved',
] as const;
export type StreamEventKind = (typeof STREAM_EVENT_KINDS)[number];

export const INCIDENT_SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'] as const;
export type IncidentSeverity = (typeof INCIDENT_SEVERITIES)[number];

/**
 * O que chega no `data:` de cada evento. Mesmo contrato do servidor
 * (`apps/api/src/realtime/events.ts`); o `at` vem em ISO/UTC e quem exibe
 * converte para o fuso do WORKSPACE, nunca o do navegador (invariante 10).
 */
export type StreamEvent = {
  id: string;
  kind: StreamEventKind;
  at: string;
  incidentId: string;
  incidentType: string;
  severity: IncidentSeverity;
  title: string;
};

export const STREAM_PATH = '/events';

/** Primeira espera da reconexão; dobra a cada tentativa até o teto. */
export const RECONNECT_BASE_MS = 1_000;
export const RECONNECT_MAX_MS = 30_000;

const streamEventSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(STREAM_EVENT_KINDS),
  at: z.string().min(1),
  incidentId: z.string().min(1),
  incidentType: z.string().min(1),
  severity: z.enum(INCIDENT_SEVERITIES),
  title: z.string(),
});

/**
 * Valida o que chegou pelo fio. Durante um deploy convivem duas versões da api,
 * e um payload de formato desconhecido não pode virar exceção dentro de um
 * `onmessage` — exceção ali sobe como erro não tratado e derruba a página
 * inteira por causa de um aviso.
 */
export function parseStreamEvent(raw: unknown): StreamEvent | null {
  if (typeof raw !== 'string') return null;
  try {
    const resultado = streamEventSchema.safeParse(JSON.parse(raw));
    return resultado.success ? resultado.data : null;
  } catch {
    return null;
  }
}

export type UseWorkspaceStreamOptions = {
  onEvent: (event: StreamEvent) => void;
  /** `false` fecha a conexão. Útil para só ouvir com a tela visível. */
  enabled?: boolean;
};

export function useWorkspaceStream(workspaceId: string, options: UseWorkspaceStreamOptions): void {
  const enabled = options.enabled ?? true;

  // Ver a armadilha 2 no cabeçalho: `onEvent` NÃO pode entrar nas dependências.
  const onEventRef = useRef(options.onEvent);
  useEffect(() => {
    onEventRef.current = options.onEvent;
  }, [options.onEvent]);

  useEffect(() => {
    if (!enabled || workspaceId === '') return;

    /**
     * Resolvido na hora de conectar, e não no topo do módulo: jsdom (a suíte) e
     * qualquer render fora do navegador não têm `EventSource`, e ler isso no
     * import quebraria o arquivo inteiro em vez de só não conectar.
     */
    const Fonte = globalThis.EventSource;
    if (typeof Fonte !== 'function') return;

    let fonte: EventSource | null = null;
    let agendada: ReturnType<typeof setTimeout> | null = null;
    let tentativas = 0;
    let cancelado = false;
    /**
     * O último evento ENTREGUE. Na reconexão automática do navegador ele vira o
     * cabeçalho `Last-Event-ID` sozinho; como aqui a reconexão é nossa (conexão
     * nova), ele vai na query — é o que faz o servidor mandar só o que faltou em
     * vez de repetir o que o usuário já viu.
     */
    let ultimoId: string | null = null;

    const conectar = () => {
      if (cancelado) return;

      const parametros = new URLSearchParams({ workspaceId });
      if (ultimoId) parametros.set('lastEventId', ultimoId);
      // `withCredentials`: o que autentica o stream é o cookie de sessão.
      const atual = new Fonte(`${API_ORIGIN}${STREAM_PATH}?${parametros.toString()}`, {
        withCredentials: true,
      });
      fonte = atual;

      atual.onopen = () => {
        // Conectou: a espera volta ao início, senão uma queda antiga penalizaria
        // para sempre uma conexão que agora está boa.
        tentativas = 0;
      };

      atual.onmessage = (mensagem: MessageEvent<unknown>) => {
        const event = parseStreamEvent(mensagem.data);
        if (!event) return;
        ultimoId = event.id;
        onEventRef.current(event);
      };

      atual.onerror = () => {
        // O `EventSource` tem reconexão própria, mas ela desiste quando a
        // resposta não é um stream válido (api reiniciando, 502 do proxy). Fechar
        // e reagendar aqui deixa UM caminho de reconexão só, sempre o nosso.
        atual.close();
        if (fonte === atual) fonte = null;
        reagendar();
      };
    };

    const reagendar = () => {
      if (cancelado || agendada) return;
      tentativas += 1;
      const teto = Math.min(RECONNECT_BASE_MS * 2 ** (tentativas - 1), RECONNECT_MAX_MS);
      /**
       * Sorteio entre metade e o teto: quando a api reinicia, TODAS as abas
       * abertas caem no mesmo instante. Sem o sorteio, todas voltariam juntas e
       * a primeira coisa que a api recém-subida receberia seria uma rajada.
       */
      const espera = teto / 2 + Math.random() * (teto / 2);
      agendada = setTimeout(() => {
        agendada = null;
        conectar();
      }, espera);
    };

    conectar();

    return () => {
      // Sem isto, cada navegação entre telas deixa um stream aberto no servidor —
      // e assinante que ninguém libera é o jeito conhecido de derrubar a api.
      cancelado = true;
      if (agendada) clearTimeout(agendada);
      agendada = null;
      fonte?.close();
      fonte = null;
    };
  }, [workspaceId, enabled]);
}
